/**
 * ErrorBoundary — Catches unhandled render errors in the component tree.
 *
 * Behaviour:
 * - On first error: shows recovery UI with a retry button that re-renders
 *   children without a full page reload (resets error state).
 * - After 1 failed retry: shows a fallback link to `fallbackHref` (default '/').
 *
 * Shared across frontends so every app's top-level routes get the same
 * recovery experience.
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCcw, Home } from 'lucide-react';

interface ErrorBoundaryProps {
  readonly children: ReactNode;
  /** Where the after-retry "go home" link points. Defaults to '/'. */
  readonly fallbackHref?: string;
  /** Label for the fallback link. Defaults to "Go to home". */
  readonly fallbackLabel?: string;
}

interface ErrorBoundaryState {
  readonly hasError: boolean;
  readonly retryCount: number;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Unhandled render error:', error, errorInfo);
  }

  private handleRetry = (): void => {
    this.setState((prev) => ({
      hasError: false,
      retryCount: prev.retryCount + 1,
    }));
  };

  override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const maxRetriesExhausted = this.state.retryCount >= 1;
    const fallbackHref = this.props.fallbackHref ?? '/';
    const fallbackLabel = this.props.fallbackLabel ?? 'Go to home';

    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md px-6">
          <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-foreground mb-2">
            Something went wrong
          </h1>
          <p className="text-base text-muted-foreground mb-8">
            An unexpected error occurred while rendering this page.
          </p>

          {maxRetriesExhausted ? (
            <a
              href={fallbackHref}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity"
            >
              <Home className="w-4 h-4" />
              {fallbackLabel}
            </a>
          ) : (
            <button
              type="button"
              onClick={this.handleRetry}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity cursor-pointer border-none"
            >
              <RefreshCcw className="w-4 h-4" />
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }
}
